
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  BarChartDatasetInterface,
  LineChartDatasetInterface, StatisticPeriodInterface,
  StatisticRepositoryInterface, UserStatisticChatTagInterface,
  UserStatisticDisplayInterface,
  UserStatisticInterface, UserStatisticTicketTagInterface,
} from '@/services/QC-V2/statistic/interfaces';
import { BaseProfileDataInterface, ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import LineChart from '@/services/QC-V2/statistic/charts/LineChart';
import { ScoreTypeInterface, ScoreTypeRepositoryInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface, ScoreGradeRepositoryInterface } from '@/services/QC-V2/score-grade/interfaces';
import BarChart from '@/services/QC-V2/statistic/charts/BarChart';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import HorizontalBarChart from '@/services/QC-V2/statistic/charts/HorizontalBarChart';

enum ChatChartItems {
  CHATS = 'chat',
  CHAT_FEEDBACKS = 'chatFeedback',
  CHAT_ESCALATIONS = 'chatEscalation',
}

enum TicketChartItems {
  TICKETS = 'ticket',
  TICKET_FEEDBACKS = 'ticketFeedback',
  TICKET_ESCALATIONS = 'ticketEscalation',
}

interface ChartItemMapInterface {
  [key: string]: { label: string, color: string };
}

interface FilterItemInterface {
  text: string,
  value: string | boolean | undefined,
}

interface FilterInterface {
  text: string,
  value: string,
  icon: string,
  items: FilterItemInterface[],
  selected: FilterItemInterface,
}

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'profileRepository',
    'statisticRepositoryV2',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
  ],
  components: {
    ChatLineChart: LineChart,
    TicketLineChart: LineChart,
    ChatBarChart: BarChart,
    TicketBarChart: BarChart,
    ChatTagBarChart: BarChart,
    ChatTagHorizontalBarChart: HorizontalBarChart,
    TicketTagBarChart: BarChart,
    TicketTagHorizontalBarChart: HorizontalBarChart,
  },
})
export default class UserStatistics extends Vue {
  private userId!: string;

  private statisticRepositoryV2!: StatisticRepositoryInterface;
  private scoreTypeRepositoryV2!: ScoreTypeRepositoryInterface;
  private scoreGradeRepositoryV2!: ScoreGradeRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;

  private profile!: BaseProfileDataInterface;
  private userStatisticsResponse!: UserStatisticInterface[];
  private overallStatistic!: UserStatisticDisplayInterface;

  private minDate!: string;

  private loading: boolean = true;

  private lineChartItemMap: ChartItemMapInterface = {
    chat: { label: 'Chats', color: '#6495ED' },
    ticket: { label: 'Archive.vue', color: '#DE3163' },
    chatFeedback: { label: 'Chat Feedbacks', color: '#CCCCFF' },
    chatEscalation: { label: 'Chat Escalations', color: '#40E0D0' },
    ticketFeedback: { label: 'Ticket Feedbacks', color: '#FFBF00' },
    ticketEscalation: { label: 'Ticket Escalations', color: '#18358b' },
  };

  private overallLineChartDataLabels: string[] = [];
  private chatTagChartDataLabels: string[] = [];
  private ticketTagChartDataLabels: string[] = [];

  private overallChatLineChartData: LineChartDatasetInterface[] = [];
  private overallChatBarChartData!: BarChartDatasetInterface;

  private chatTagChartData!: BarChartDatasetInterface;
  private overallChatChartFooter: string = '';

  private overallTicketLineChartData: LineChartDatasetInterface[] = [];
  private overallTicketBarChartData!: BarChartDatasetInterface;
  private ticketTagChartData!: BarChartDatasetInterface;
  private overallTicketChartFooter: string = '';

  private scoreTypesMap: { [key: number]: ScoreTypeInterface } = {};
  private scoreGradesMap: { [key: number]: ScoreGradeInterface } = {};
  private tagsMap: { [key: number]: TagInterface } = {};

  private filter: FilterInterface = {
    text: 'Date',
    value: 'startDate',
    icon: 'mdi-calendar',
    items: [
      { text: 'TODAY', value: 'today' },
      { text: 'YESTERDAY', value: 'yesterday' },
      { text: 'LAST 7 DAYS', value: '7 days' },
      { text: 'LAST 30 DAYS', value: '30 days' },
      { text: 'LAST MONTH', value: 'last month' },
      { text: 'CURRENT MONTH', value: 'current month' },
      { text: 'CUSTOM PERIOD', value: 'custom period' },
    ],
    selected: { text: 'LAST 7 DAYS', value: '7 days' },
  };

  private customDates: string[] = [];

  private show: boolean = false;

  private period: StatisticPeriodInterface | undefined = undefined;

  private startDate: Date | undefined;

  private endDate: Date | undefined;

  private cutChatTagCharts: boolean = true;

  private cutTicketTagCharts: boolean = true;

  private async created(): Promise<void> {
    this.userId = this.$route.params.userId;
    this.profile = await this.profileRepository.getBaseProfileDataByUserId(this.userId);
    await this.fetchScores();
    await this.fetchTags();
    await this.applyFilter();
    this.setMinDate();
    this.loading = false;
  }

  private async initialize(startDate?: Date, endDate?: Date): Promise<void> {
    this.startDate = startDate;
    this.endDate = endDate;

    try {
      this.userStatisticsResponse = await this.statisticRepositoryV2.findUserStatistic(this.userId, this.period);
      this.clearData();
      this.aggregateOverallStatistic();
      this.aggregateOverallChartLabels(startDate, endDate);
      this.aggregateOverallChatChartData();
      this.aggregateChatTagChartData();
      this.aggregateOverallTicketChartData();
      this.aggregateTicketTagChartData();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private setMinDate(): void {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    this.minDate = date.toISOString();
  }

  private clearData(): void {
    this.overallStatistic = {
      userId: this.userId,
      chats: 0,
      tickets: 0,
      chatFeedbacks: 0,
      chatEscalations: 0,
      ticketFeedbacks: 0,
      ticketEscalations: 0,
    };
    this.overallLineChartDataLabels = [];
    this.overallChatLineChartData = [];
    this.overallChatChartFooter = '';
    this.overallTicketLineChartData = [];
    this.overallTicketChartFooter = '';
    this.overallChatBarChartData =
      this.chatTagChartData =
        this.overallTicketBarChartData =
          this.ticketTagChartData = {
            labels: [],
            data: [],
            borderColor: [],
            backgroundColor: [],
            blobTexts: [],
          };
  }

  private aggregateOverallStatistic(): void {
    this.userStatisticsResponse.forEach((userStatistic) => {
      this.overallStatistic = {
        userId: userStatistic.userId,
        chats: this.overallStatistic.chats + (userStatistic.chat?.count || 0),
        tickets: this.overallStatistic.tickets + (userStatistic.ticket?.count || 0),
        chatFeedbacks: this.overallStatistic.chatFeedbacks + (userStatistic.chatFeedback?.count || 0),
        chatEscalations: this.overallStatistic.chatEscalations + (userStatistic.chatEscalation?.count || 0),
        ticketFeedbacks: this.overallStatistic.ticketFeedbacks + (userStatistic.ticketFeedback?.count || 0),
        ticketEscalations: this.overallStatistic.ticketEscalations + (userStatistic.ticketEscalation?.count || 0),
      };
    });
  }

  private aggregateOverallChartLabels(startDate?: Date, endDate?: Date): void {
    this.overallLineChartDataLabels = this.getDatesArray(startDate, endDate);
  }

  private aggregateOverallChatChartData(): void {
    Object.values(ChatChartItems).forEach((chartItem) => {
      this.overallChatLineChartData.push({
        label: this.lineChartItemMap[chartItem].label,
        item: chartItem,
        data: new Array(this.overallLineChartDataLabels.length).fill(0),
        blobTexts: new Array(this.overallLineChartDataLabels.length).fill(''),
        tension: 0,
        fill: false,
        borderColor: this.lineChartItemMap[chartItem].color,
        backgroundColor: this.lineChartItemMap[chartItem].color,
      });
    });

    // Used For Creating Footer Period Average Scores for each entity...
    const periodScores: {
      [key: string]: {
        count: number, scores: Array<{ scoreTypeId: number, value: number }>,
      },
    } = {};

    this.userStatisticsResponse.forEach((userStatistic) => {
      Object.values(ChatChartItems).forEach((chartItem) => {
        const index = this.overallLineChartDataLabels.findIndex((label) => label === String(userStatistic.date));
        if (index === -1) {
          return;
        }

        const dataIndex = this.overallChatLineChartData.findIndex((data) => data.item === chartItem);
        if (dataIndex === -1) {
          return;
        }

        this.overallChatLineChartData[dataIndex].data[index] += userStatistic[chartItem]?.count || 0;
        if (!periodScores[chartItem]?.count) {
          periodScores[chartItem] = {
            count: 0,
            scores: [],
          };
        }
        periodScores[chartItem].count += userStatistic[chartItem]?.count || 0;

        if (userStatistic[chartItem]?.scores && userStatistic[chartItem]?.scores.length) {
          const scores = this.extractScores(userStatistic, chartItem, periodScores);

          this.overallChatLineChartData[dataIndex].blobTexts[index] = scores.reduce((res, score) =>
            `${res}\n ${this.scoreTypesMap[score.scoreTypeId].label} - ${(score.value / (userStatistic[chartItem]?.count || 1)).toFixed(2)}`, '');
        }
      });
    });

    this.overallChatChartFooter = Object.keys(periodScores).reduce((r, scoreKey) => {
      if (periodScores[scoreKey].scores.length === 0) {
        return r;
      }

      return `${r} | ${this.lineChartItemMap[scoreKey].label}: ${periodScores[scoreKey].scores.reduce((res, score) =>
        `${res} ${this.scoreTypesMap[score.scoreTypeId].label} - ${(score.value / (periodScores[scoreKey].count || 1)).toFixed(2)}`, '')} |`;
    }, '');

    if (this.period && this.period.startDate === this.period.endDate) {
      this.overallChatBarChartData = this.createHorizontalDataset(this.overallChatLineChartData);
    }
  }

  private aggregateOverallTicketChartData(): void {
    Object.values(TicketChartItems).forEach((chartItem) => {
      this.overallTicketLineChartData.push({
        label: this.lineChartItemMap[chartItem].label,
        item: chartItem,
        data: new Array(this.overallLineChartDataLabels.length).fill(0),
        blobTexts: new Array(this.overallLineChartDataLabels.length).fill(''),
        tension: 0,
        fill: false,
        borderColor: this.lineChartItemMap[chartItem].color,
        backgroundColor: this.lineChartItemMap[chartItem].color,
      });
    });

    // Used For Creating Footer Period Average Scores for each entity...
    const periodScores: {
      [key: string]: {
        count: number, scores: Array<{ scoreTypeId: number, value: number }>,
      },
    } = {};

    this.userStatisticsResponse.forEach((userStatistic) => {
      Object.values(TicketChartItems).forEach((chartItem) => {
        const index = this.overallLineChartDataLabels.findIndex((label) => label === String(userStatistic.date));
        if (index === -1) {
          return;
        }

        const dataIndex = this.overallTicketLineChartData.findIndex((data) => data.item === chartItem);
        if (dataIndex === -1) {
          return;
        }

        this.overallTicketLineChartData[dataIndex].data[index] += userStatistic[chartItem]?.count || 0;
        if (!periodScores[chartItem]?.count) {
          periodScores[chartItem] = {
            count: 0,
            scores: [],
          };
        }
        periodScores[chartItem].count += userStatistic[chartItem]?.count || 0;
        if (userStatistic[chartItem]?.scores && userStatistic[chartItem]?.scores.length) {
          const scores = this.extractScores(userStatistic, chartItem, periodScores);
          this.overallTicketLineChartData[dataIndex].blobTexts[index] = scores.reduce((res, score) =>
            `${res}\n ${this.scoreTypesMap[score.scoreTypeId].label} - ${(score.value / (userStatistic[chartItem]?.count || 1)).toFixed(2)}`, '');
        }
      });
    });


    this.overallTicketChartFooter = Object.keys(periodScores).reduce((r, scoreKey) => {
      if (periodScores[scoreKey].scores.length === 0) {
        return r;
      }

      return `${r} | ${this.lineChartItemMap[scoreKey].label}: ${periodScores[scoreKey].scores.reduce((res, score) =>
        `${res} ${this.scoreTypesMap[score.scoreTypeId].label} - ${(score.value / (periodScores[scoreKey].count || 1)).toFixed(2)}`, '')} |`;
    }, '');

    if (this.period && this.period.startDate === this.period.endDate) {
      this.overallTicketBarChartData = this.createHorizontalDataset(this.overallTicketLineChartData);
    }
  }

  private extractScores(
    userStatistic: UserStatisticInterface,
    chartItem: ChatChartItems | TicketChartItems,
    periodScores: { [key: string]: { count: number, scores: Array<{ scoreTypeId: number, value: number }> } },
  ): Array<{ scoreTypeId: number, value: number }> {
    const scores: Array<{ scoreTypeId: number, value: number }> = [];
    userStatistic[chartItem]?.scores.forEach((score) => {
      const scoreTypeIndex = scores.findIndex((s) => s.scoreTypeId === score.scoreTypeId);
      if (scoreTypeIndex === -1) {
        scores.push({
          scoreTypeId: score.scoreTypeId,
          value: score.count * this.scoreGradesMap[score.scoreGradeId].value,
        });
      } else {
        scores[scoreTypeIndex].value += score.count * this.scoreGradesMap[score.scoreGradeId].value;
      }

      const periodIndex = periodScores[chartItem].scores.findIndex((s) => s.scoreTypeId === score.scoreTypeId);
      if (periodIndex === -1) {
        periodScores[chartItem].scores.push({
          scoreTypeId: score.scoreTypeId,
          value: score.count * this.scoreGradesMap[score.scoreGradeId].value,
        });
      } else {
        periodScores[chartItem].scores[periodIndex].value +=
          score.count * this.scoreGradesMap[score.scoreGradeId].value;
      }
    });

    return scores;
  }

  private createHorizontalDataset(lineChartData: LineChartDatasetInterface[]): BarChartDatasetInterface {
    const dataset: BarChartDatasetInterface = {
      labels: [],
      data: [],
      borderColor: [],
      backgroundColor: [],
      blobTexts: [],
    };

    lineChartData.forEach((chartData) => {
      dataset.labels.push(chartData.label);
      dataset.data.push(...chartData.data);
      dataset.backgroundColor.push(chartData.backgroundColor);
      dataset.borderColor.push(chartData.borderColor);
      dataset.blobTexts.push(...chartData.blobTexts);
    });

    return dataset;
  }

  private switchChatTagChart(): void {
    this.cutChatTagCharts = !this.cutChatTagCharts;
    this.chatTagChartDataLabels = [];
    this.chatTagChartData = {} as BarChartDatasetInterface;
    this.aggregateChatTagChartData();
  }

  private aggregateChatTagChartData(): void {
    let tags: UserStatisticChatTagInterface[] = [];
    this.userStatisticsResponse.map((statistic) => statistic.chat?.tags).flat().forEach((tag) => {
      if (!tag) {
        return;
      }

      const index = tags.findIndex((t) => t.tagId === tag.tagId);
      if (index === -1) {
        tags.push(tag);
        return;
      }

      tags[index].count += tag.count;
    });

    tags = tags.sort((tA, tB) => tB.count - tA.count);
    if (this.cutChatTagCharts) {
      tags = tags.slice(0, 10);
    }

    this.chatTagChartData = this.createHorizontalTagDataset(tags);
  }

  private switchTicketTagChart(): void {
    this.cutTicketTagCharts = !this.cutTicketTagCharts;
    this.ticketTagChartDataLabels = [];
    this.ticketTagChartData = {
      labels: [],
      data: [],
      borderColor: [],
      backgroundColor: [],
      blobTexts: [],
    };
    this.aggregateTicketTagChartData();
  }

  private aggregateTicketTagChartData(): void {
    let tags: UserStatisticTicketTagInterface[] = [];
    this.userStatisticsResponse.map((statistic) => statistic.ticket?.tags).flat().forEach((tag) => {
      if (!tag) {
        return;
      }

      const index = tags.findIndex((t) => t.tagId === tag.tagId);
      if (index === -1) {
        tags.push(tag);
        return;
      }

      tags[index].count += tag.count;
    });

    tags = tags.sort((tA, tB) => tB.count - tA.count);
    if (this.cutTicketTagCharts) {
      tags = tags.slice(0, 10);
    }

    this.ticketTagChartData = this.createHorizontalTagDataset(tags);
  }

  private createHorizontalTagDataset(
    tags: UserStatisticChatTagInterface[] | UserStatisticTicketTagInterface[],
  ): BarChartDatasetInterface {
    const dataset: BarChartDatasetInterface = {
      labels: [],
      data: [],
      backgroundColor: [],
      borderColor: [],
      blobTexts: [],
    };

    tags.forEach((tag) => {
      const tagEntity = this.tagsMap[tag.tagId];
      dataset.labels.push(tagEntity.label);
      dataset.data.push(tag.count);
      dataset.borderColor.push(tagEntity.color);
      dataset.backgroundColor.push(tagEntity.color);
    });

    return dataset;
  }

  private getDatesArray(startDate: Date = new Date(), endDate: Date = new Date()): string[] {
    const interval = 1000 * 60 * 60 * 24;

    const duration = endDate.getTime() - startDate.getTime();
    const steps = duration / interval;

    return Array.from(
      { length: steps + 1 }, (v, i) => new Date(startDate.valueOf() + (interval * i)),
    ).sort((dateA, dateB) => dateA.getTime() - dateB.getTime())
      .map((date) => date.toLocaleString('sv-se').substr(0, 10));
  }

  private async fetchScores(): Promise<void> {
    const scoreTypes = await this.scoreTypeRepositoryV2.getAll();
    scoreTypes.forEach((scoreType) => {
      this.scoreTypesMap[scoreType.id] = scoreType;
    });

    const scoreGrades = await this.scoreGradeRepositoryV2.getAll();
    scoreGrades.forEach((scoreGrade) => {
      this.scoreGradesMap[scoreGrade.id] = scoreGrade;
    });
  }

  private async fetchTags(): Promise<void> {
    const tags = await this.tagRepositoryV2.getAllTags();
    tags.forEach((tag) => {
      this.tagsMap[tag.id] = tag;
    });
  }

  private keepCustomPeriodActive(filter: FilterInterface): void {
    setTimeout(() => {
      if (filter.selected && filter.selected.value === 'custom period') {
        filter.selected = { text: 'CUSTOM PERIOD', value: 'custom period' };
      }
    }, 10);
  }

  private get datesRangeText(): string {
    return this.customDates.join(' ~ ');
  }

  @Watch('filter.selected')
  private async datesFilter(): Promise<void> {
    const sameDateFilter = this.customDates.includes(String(this.period?.startDate)) && this.customDates.includes(String(this.period?.endDate));
    if (this.filter?.selected?.value !== 'custom period' || this.customDates.length < 2 || sameDateFilter) {
      return;
    }

    await this.applyFilter();
  }

  private async applyFilter(): Promise<void> {
    this.period = this.getDateFilterData();
    let startDate: Date | undefined;
    let endDate: Date | undefined;
    if (this.period?.startDate && this.period.endDate) {
      startDate = new Date(this.period.startDate);
      endDate = new Date(this.period.endDate);
    }

    await this.initialize(startDate, endDate);
  }

  private getDateFilterData(): { startDate: string, endDate: string } | undefined {
    if (!this.filter.selected || !this.filter.selected.value) {
      return undefined;
    }

    const selected = this.filter.selected.value as string;

    let startDate: Date = new Date();
    let endDate: Date = new Date();
    let filterApplied = false;

    if (selected === 'today') {
      this.customDates = [];
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();

      filterApplied = true;
    }

    if (selected === 'yesterday') {
      this.customDates = [];
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
      endDate.setHours(23, 59, 59, 59);

      filterApplied = true;
    }

    if (selected.endsWith('days')) {
      this.customDates = [];
      const [count] = selected.split(' ');
      startDate = new Date();
      startDate.setDate(startDate.getDate() - Number(count));
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'last month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);

      endDate = new Date(date.getFullYear(), date.getMonth(), 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'current month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth(), 1);

      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }


    if (selected === 'custom period') {
      if (this.customDates.length < 2) {
        return undefined;
      }

      const [startDateStr, endDateStr] = this.customDates;
      startDate = new Date(startDateStr);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(endDateStr);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (!filterApplied) {
      return undefined;
    }

    if (startDate > endDate) {
      const temp = startDate;
      startDate = endDate;
      endDate = temp;
    }

    return { startDate: this.convertDateToString(startDate), endDate: this.convertDateToString(endDate) };
  }

  private convertDateToString(date: Date): string {
    return date.toLocaleString('sv-se').substr(0, 10);
  }

  private preventClose(event: PointerEvent, item: FilterItemInterface): void {
    if (item.value === 'custom period') {
      this.filter.selected = item;
      event.stopPropagation();
      event.preventDefault();
      this.keepCustomPeriodActive(this.filter);
    }
  }
}
